import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, Shield, Leaf, Award, Target, ChevronRight, Flower2 } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { DraggableProductImage } from '../components/DraggableProductImage';
import { MobileOptimizedProductImage } from '../components/MobileOptimizedProductImage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/Tabs';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import SubscriptionSelector from '../components/SubscriptionSelector';
import { getSellingPlans } from '../lib/shopify-subscriptions';
import { products } from '../data/products';
import { useCart } from '../context/CartContext';
import { useShopifyPrice } from '../hooks/useShopifyPrice';
import { useAnalytics } from '../hooks/useSupabase';
import { getProductColors } from '../data/productColors';

function CountUp({ end, duration = 2, suffix = '' }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !isVisible) setIsVisible(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    let frame;
    const animate = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / (duration * 1000), 1);
      setCount(Math.floor(end * progress));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [end, duration, isVisible]);

  return <span ref={ref} className="tabular-nums">{count}{suffix}</span>;
}

const ingredientIcons = {
  'Lions Mane': '/icons/lionsmane.png',
  "Lion's Mane Extract": '/icons/lionsmane.png',
  'Organic Lions Mane Extract': '/icons/lionsmane.png',
  'Cordyceps Powder': '/icons/cordycep.png',
  'Cordyceps Extract': '/icons/cordycep.png',
  'Organic Cordyceps Extract': '/icons/cordycep.png',
  'Reishi Mushroom Extract': '/icons/reishi.png',
  'Reishi Extract': '/icons/reishi.png',
  'Organic Reishi Mushroom Extract': '/icons/reishi.png',
  'Shiitake Mushroom Extract': '/icons/shiitake.png',
  'Shiitake Extract': '/icons/shiitake.png',
  'Organic Shiitake Extract': '/icons/shiitake.png',
  'Proprietary Blend': '/icons/proprietary-blend.png',
  'Complete Vitamin Complex': '/icons/complete-vitamin-complex.png',
  'Essential Minerals': '/icons/essential-minerals.png',
  'Choline': '/icons/choline.png',
  'Biotin': '/icons/biotin.png',
  'Organic Turkey Tail Extract': '/icons/organic-turkey-tail-extract.png',
  'Organic Maitake Extract': '/icons/organic-maitake-extract.png',
  'Organic Chaga Extract': '/icons/organic-chaga-extract.png',
  'Herbal Adaptogen Blend': '/icons/herbal-adaptogen-blend.png',
};

const whyFeatures = [
  { title: 'Cognitive Enhancement', description: 'Boost mental clarity, focus, and memory with our nootropic blend.' },
  { title: 'Sustained Energy', description: 'Natural energy without jitters or crashes throughout your day.' },
  { title: 'Immune Defense', description: "Strengthen your body's natural defenses with beta-glucans." },
  { title: 'Stress Resilience', description: 'Adaptogenic properties help your body manage daily stress.' },
  { title: '100% Organic', description: 'Pure, organic mushrooms with no synthetic additives.' },
  { title: 'Naturally Studied', description: 'Doses based on scientific research for maximum efficacy.' },
];

export function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const product = products[productId];
  const [quantity, setQuantity] = useState(1);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [activeIngredient, setActiveIngredient] = useState(0);
  const [purchaseType, setPurchaseType] = useState('onetime');
  const [selectedSellingPlanId, setSelectedSellingPlanId] = useState(null);
  const [sellingPlans, setSellingPlans] = useState([]);
  const [, setLoadingSellingPlans] = useState(true);

  const colors = getProductColors(productId);

  const { isCartOpen, setIsCartOpen, cartItems, addToCart, updateQuantity, removeItem, cartItemsCount } = useCart();
  const { price, variantId, loading } = useShopifyPrice(productId);
  const { trackView, trackCartAdd } = useAnalytics();

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  useEffect(() => {
    const fetchSellingPlans = async () => {
      setLoadingSellingPlans(true);
      if (productId) {
        const plans = await getSellingPlans(productId);
        setSellingPlans(plans);
      }
      setLoadingSellingPlans(false);
    };
    fetchSellingPlans();
  }, [productId, variantId]);

  useEffect(() => {
    setQuantity(1);
    setPurchaseType('onetime');
    setSelectedSellingPlanId(null);
    setActiveIngredient(0);
  }, [productId]);

  const handlePurchaseTypeChange = useCallback((type, planId) => {
    if (type === 'subscription' && !planId) {
      setPurchaseType('onetime');
      setSelectedSellingPlanId(null);
      return;
    }

    setPurchaseType(type);
    setSelectedSellingPlanId(type === 'subscription' ? planId : null);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  useEffect(() => {
    if (productId) trackView(productId);
  }, [productId, trackView]);

  useEffect(() => {
    if (!product?.ingredients?.length) return;
    const interval = setInterval(() => {
      setActiveIngredient((prev) => (prev + 1) % product.ingredients.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [product?.ingredients?.length]);

  useEffect(() => {
    if (!product?.ingredients?.length) return;

    const iconPaths = [...new Set(
      product.ingredients
        .map((ingredient) => ingredientIcons[ingredient.name])
        .filter(Boolean)
    )];

    iconPaths.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [product?.ingredients]);

  if (!product) { navigate('/'); return null; }

  const basePrice = price || product.price;
  const displayPrice = purchaseType === 'subscription' ? basePrice * 0.9 : basePrice;
  const savingsAmount = purchaseType === 'subscription' ? basePrice * 0.1 : 0;

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />

      {/* Hero */}
      <section className="pt-32 pb-16" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <button
            onClick={() => navigate('/products')}
            className="flex items-center gap-2 text-sm mb-12 transition-opacity hover:opacity-60"
            style={{ color: 'rgba(26,42,26,0.45)' }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All Products
          </button>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Product Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              {product.images ? (
                isTouchDevice ? (
                  <MobileOptimizedProductImage images={product.images} title={product.title} />
                ) : (
                  <DraggableProductImage images={product.images} title={product.title} />
                )
              ) : (
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full max-w-md mx-auto drop-shadow-xl object-contain"
                />
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-5" style={{ color: '#B8704D' }}>
                {product.subtitle}
              </p>
              <h1 className="text-5xl md:text-6xl font-light leading-tight mb-6" style={{ color: colors.primary }}>
                {product.title}
              </h1>
              <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(26,42,26,0.55)', maxWidth: '46ch' }}>
                {product.description}
              </p>

              <div className="h-px mb-8" style={{ background: 'rgba(26,42,26,0.08)' }} />

              {/* Price */}
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-5xl font-light" style={{ color: '#1A2A1A' }}>
                  {loading ? (
                    <span className="text-2xl" style={{ color: 'rgba(26,42,26,0.4)' }}>—</span>
                  ) : (
                    `$${displayPrice.toFixed(2)}`
                  )}
                </span>
                {!loading && purchaseType === 'subscription' && (
                  <span className="text-base line-through" style={{ color: 'rgba(26,42,26,0.3)' }}>
                    ${basePrice.toFixed(2)}
                  </span>
                )}
                <span className="text-xs tracking-widest uppercase" style={{ color: 'rgba(26,42,26,0.4)' }}>
                  {product.supplyDays}-day supply
                </span>
              </div>

              {/* Subscription Selector */}
              <SubscriptionSelector
                key={`subscription-${productId}`}
                product={product}
                sellingPlans={sellingPlans}
                purchaseType={purchaseType}
                selectedSellingPlanId={selectedSellingPlanId}
                onPurchaseTypeChange={handlePurchaseTypeChange}
              />

              {/* Quantity */}
              <div className="flex items-center gap-5 my-6">
                <span className="text-xs tracking-widest uppercase" style={{ color: 'rgba(26,42,26,0.45)' }}>Qty</span>
                <div className="flex items-center border" style={{ borderColor: 'rgba(26,42,26,0.15)' }}>
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2.5 text-sm transition-colors"
                    style={{ color: '#1A2A1A' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(26,42,26,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    −
                  </button>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={quantity}
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      className="px-6 py-2.5 text-sm border-x text-center min-w-[3rem]"
                      style={{ borderColor: 'rgba(26,42,26,0.15)', color: '#1A2A1A' }}
                    >
                      {quantity}
                    </motion.span>
                  </AnimatePresence>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-4 py-2.5 text-sm transition-colors"
                    style={{ color: '#1A2A1A' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(26,42,26,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToCart({ ...product, price: displayPrice, sellingPlanId: selectedSellingPlanId, isSubscription: purchaseType === 'subscription' }, quantity, variantId);
                  trackCartAdd(productId, quantity);
                }}
                disabled={loading}
                className="w-full py-4 text-sm font-medium tracking-wide transition-colors mb-3"
                style={{ background: '#1A2A1A', color: '#FAF9F6' }}
                onMouseEnter={e => !loading && (e.currentTarget.style.background = '#243424')}
                onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
              >
                {loading ? 'Loading...' : `Add to Cart — $${(displayPrice * quantity).toFixed(2)}`}
                {!loading && purchaseType === 'subscription' && savingsAmount > 0 && (
                  <span className="ml-2 text-xs opacity-70">(Save ${(savingsAmount * quantity).toFixed(2)})</span>
                )}
              </button>

              <button
                onClick={() => navigate('/bundle')}
                className="w-full py-3 text-sm tracking-wide transition-colors border"
                style={{ borderColor: 'rgba(26,42,26,0.15)', color: '#B8704D', background: 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(184,112,77,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                Bundle all 3 and save up to 25% →
              </button>

              {/* In Stock */}
              <div className="flex items-center gap-2 mt-4 mb-2">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#22c55e' }} />
                <span className="text-xs" style={{ color: 'rgba(26,42,26,0.5)' }}>In Stock — Ships within 24 hours</span>
              </div>

              {/* Feature Strip */}
              <div className="mt-6 pt-6 grid grid-cols-3 border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                {[
                  { label: 'Supply', value: `${product.supplyDays} Days` },
                  { label: 'Quality', value: 'Lab Tested' },
                  { label: 'Shipping', value: 'Free USA' },
                ].map((f, i) => (
                  <div
                    key={i}
                    className={`px-5 ${i < 2 ? 'border-r' : ''}`}
                    style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                  >
                    <p className="text-[10px] tracking-widest uppercase mb-1" style={{ color: 'rgba(26,42,26,0.4)' }}>{f.label}</p>
                    <p className="text-sm font-medium" style={{ color: '#1A2A1A' }}>{f.value}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-5 border-y" style={{ borderColor: 'rgba(26,42,26,0.06)', background: '#F0EDE6' }}>
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-8">
            {[
              'USDA Organic',
              '3rd Party Tested',
              'GMP Certified',
              'Made in USA',
              'Non-GMO',
              'Gluten Free',
            ].map((badge) => (
              <span key={badge} className="flex items-center gap-2 text-xs tracking-wide" style={{ color: 'rgba(26,42,26,0.6)' }}>
                <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Ingredients */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Formula
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#1A2A1A' }}>
              Naturally proven ingredients.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-0 border" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
            {/* Left: Ingredient display */}
            <div className="relative min-h-[420px] border-b md:border-b-0 md:border-r" style={{ borderColor: 'rgba(26,42,26,0.08)', background: '#F0EDE6' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIngredient}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center"
                >
                  {ingredientIcons[product.ingredients[activeIngredient].name] ? (
                    <img
                      src={ingredientIcons[product.ingredients[activeIngredient].name]}
                      alt={product.ingredients[activeIngredient].name}
                      className="w-32 h-32 object-contain mb-8"
                      loading="eager"
                      decoding="async"
                    />
                  ) : (
                    <Flower2 className="w-32 h-32 mb-8" style={{ color: '#B8704D' }} />
                  )}
                  <p className="text-5xl font-light mb-2" style={{ color: '#1A2A1A' }}>
                    {product.ingredients[activeIngredient].amount}
                  </p>
                  <p className="text-base font-medium mb-4" style={{ color: '#1A2A1A' }}>
                    {product.ingredients[activeIngredient].name}
                  </p>
                  <p className="text-xs leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)', maxWidth: '32ch' }}>
                    {product.ingredients[activeIngredient].description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Dot indicators */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {product.ingredients.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIngredient(i)}
                    className="h-0.5 transition-all duration-300"
                    style={{
                      width: i === activeIngredient ? '24px' : '6px',
                      background: i === activeIngredient ? '#B8704D' : 'rgba(26,42,26,0.2)',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Right: Ingredient list */}
            <div>
              {product.ingredients.map((ingredient, i) => (
                <motion.button
                  key={i}
                  onClick={() => setActiveIngredient(i)}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full flex items-center justify-between px-8 py-6 border-b text-left transition-all"
                  style={{
                    borderColor: 'rgba(26,42,26,0.08)',
                    borderLeft: i === activeIngredient ? '2px solid #B8704D' : '2px solid transparent',
                    background: i === activeIngredient ? 'rgba(184,112,77,0.04)' : 'transparent',
                  }}
                >
                  <div>
                    <p className="text-sm font-medium mb-0.5" style={{ color: i === activeIngredient ? '#B8704D' : '#1A2A1A' }}>
                      {ingredient.name}
                    </p>
                    <p className="text-xs" style={{ color: 'rgba(26,42,26,0.4)' }}>
                      {ingredient.amount}
                    </p>
                  </div>
                  <ChevronRight
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: i === activeIngredient ? '#B8704D' : 'rgba(26,42,26,0.25)' }}
                  />
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24" style={{ background: '#E8DCC4' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Community Results
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#1A2A1A' }}>
              Real people. Real results.
            </h2>
          </motion.div>

          <div className="grid grid-cols-3 border-t" style={{ borderColor: 'rgba(26,42,26,0.12)' }}>
            {[
              { n: 87, suffix: '%', label: 'Report more energy' },
              { n: 92, suffix: '%', label: 'Improved mental clarity' },
              { n: 95, suffix: '%', label: 'Would recommend' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: i * 0.1 }}
                className={`px-3 py-6 md:px-8 md:py-10 ${i < 2 ? 'border-r' : ''}`}
                style={{ borderColor: 'rgba(26,42,26,0.12)' }}
              >
                <p className="text-2xl md:text-7xl font-light mb-1 md:mb-3" style={{ color: '#1A2A1A' }}>
                  <CountUp end={stat.n} suffix={stat.suffix} duration={2} />
                </p>
                <p className="text-[9px] md:text-xs tracking-widest uppercase leading-tight" style={{ color: 'rgba(26,42,26,0.5)' }}>
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 max-w-2xl space-y-8">
            {[
              { label: 'Immune System Support', value: 94 },
              { label: 'Stress Management', value: 89 },
              { label: 'Overall Wellbeing', value: 96 },
              { label: 'Sleep Quality', value: 85 },
            ].map((bar, i) => (
              <div key={i}>
                <div className="flex justify-between mb-3">
                  <span className="text-sm" style={{ color: '#1A2A1A' }}>{bar.label}</span>
                  <span className="text-sm font-medium" style={{ color: '#B8704D' }}>{bar.value}%</span>
                </div>
                <div className="h-px relative" style={{ background: 'rgba(26,42,26,0.15)' }}>
                  <motion.div
                    className="absolute left-0 top-0 h-px"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${bar.value}%` }}
                    transition={{ duration: 1.2, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    style={{ background: '#B8704D' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Berleto */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="md:sticky md:top-32"
            >
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                Why Berleto
              </p>
              <h2 className="text-4xl md:text-5xl font-light leading-tight" style={{ color: '#1A2A1A' }}>
                Six reasons.<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>No compromises.</span>
              </h2>
            </motion.div>

            <div>
              {whyFeatures.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-6 py-7 border-b"
                  style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                >
                  <span className="text-xs font-medium tracking-widest mt-0.5 flex-shrink-0 w-6" style={{ color: '#B8704D' }}>
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="text-base font-medium mb-2" style={{ color: '#1A2A1A' }}>{f.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)' }}>{f.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <Tabs defaultValue="benefits" className="w-full">
            <div className="border-b mb-12" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
              <TabsList className="flex gap-0 bg-transparent p-0">
                {['benefits', 'usage', 'science'].map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className="px-6 py-3 text-xs tracking-[0.2em] uppercase border-b-2 border-transparent transition-all data-[state=active]:border-[#B8704D] data-[state=active]:text-[#1A2A1A] text-[rgba(26,42,26,0.45)] bg-transparent rounded-none hover:text-[#1A2A1A]"
                  >
                    {tab === 'benefits' ? 'Benefits' : tab === 'usage' ? 'Usage' : 'Science'}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value="benefits">
              <div className="grid md:grid-cols-2 gap-0 border" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                {product.benefits.map((benefit, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.06 }}
                    className={`flex items-start gap-4 px-8 py-6 border-b ${i % 2 === 0 ? 'md:border-r' : ''}`}
                    style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                  >
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#B8704D' }} />
                    <p className="text-sm leading-relaxed" style={{ color: '#1A2A1A' }}>{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="usage">
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-3 border" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                  {[
                    { time: 'Morning', detail: 'Take 2 capsules with breakfast' },
                    { time: 'Afternoon', detail: 'Feel sustained energy and focus' },
                    { time: 'Evening', detail: 'Enjoy restful, quality sleep' },
                  ].map((step, i) => (
                    <div
                      key={i}
                      className={`px-3 py-6 md:px-8 md:py-8 ${i < 2 ? 'border-r' : ''}`}
                      style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                    >
                      <p className="text-[10px] md:text-xs tracking-widest uppercase mb-2 md:mb-3" style={{ color: '#B8704D' }}>{step.time}</p>
                      <p className="text-xs md:text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.6)' }}>{step.detail}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 border-t pt-8" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                  <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(26,42,26,0.6)' }}>{product.usage}</p>
                  <p className="text-xs font-medium tracking-widest uppercase mb-4" style={{ color: '#1A2A1A' }}>Important Notes</p>
                  <ul className="space-y-3">
                    {product.warnings.map((warning, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm" style={{ color: 'rgba(26,42,26,0.55)' }}>
                        <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: '#B8704D' }} />
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="science">
              <div className="grid md:grid-cols-2 gap-0 border" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                {[
                  { title: 'Research Study 2023', result: '87% improvement in cognitive function', participants: '147 participants', duration: '12 weeks' },
                  { title: 'Energy & Focus Trial', result: '3.2x better sustained energy', participants: '89 participants', duration: '8 weeks' },
                  { title: 'Immune Response Study', result: '45% stronger immune markers', participants: '203 participants', duration: '16 weeks' },
                  { title: 'Stress Adaptation Research', result: '62% reduction in stress levels', participants: '156 participants', duration: '6 weeks' },
                ].map((study, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.08 }}
                    className={`px-8 py-8 border-b ${i % 2 === 0 ? 'md:border-r' : ''}`}
                    style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                  >
                    <p className="text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(26,42,26,0.4)' }}>{study.title}</p>
                    <p className="text-2xl font-light mb-4" style={{ color: '#B8704D' }}>{study.result}</p>
                    <div className="flex gap-6 text-xs" style={{ color: 'rgba(26,42,26,0.45)' }}>
                      <span>{study.participants}</span>
                      <span>{study.duration}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-24" style={{ background: '#1A2A1A' }} data-bubble-theme="light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Standards
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#FAF9F6' }}>
              Uncompromising quality.
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 border-t" style={{ borderColor: 'rgba(139,154,139,0.15)' }}>
            {[
              { icon: Shield, title: '3rd Party Tested', desc: 'Every batch verified for purity and potency' },
              { icon: Award, title: 'GMP Certified', desc: 'Manufactured in FDA-registered facilities' },
              { icon: Leaf, title: 'USDA Organic', desc: '100% organic, non-GMO ingredients' },
              { icon: Target, title: 'Naturally Studied', desc: 'Doses based on peer-reviewed research' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className={`px-8 py-10 border-b md:border-b-0 ${i < 3 ? 'md:border-r' : ''}`}
                style={{ borderColor: 'rgba(139,154,139,0.15)' }}
              >
                <item.icon className="w-6 h-6 mb-5" style={{ color: '#B8704D' }} />
                <h3 className="text-sm font-medium mb-2" style={{ color: '#FAF9F6' }}>{item.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(139,154,139,0.65)' }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Get Started
              </p>
              <h2 className="text-4xl md:text-5xl font-light mb-4" style={{ color: '#1A2A1A' }}>
                {product.title}.<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>Ready when you are.</span>
              </h2>
              <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(26,42,26,0.5)', maxWidth: '42ch' }}>
                {product.supplyDays}-day supply. Subscribe and save 10%. Free shipping within the USA.
              </p>

              <div className="mb-6">
                <span className="text-5xl font-light" style={{ color: '#1A2A1A' }}>
                  {loading ? '—' : `$${displayPrice.toFixed(2)}`}
                </span>
              </div>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToCart({ ...product, price: displayPrice, sellingPlanId: selectedSellingPlanId, isSubscription: purchaseType === 'subscription' }, 1, variantId);
                  trackCartAdd(productId, 1);
                }}
                disabled={loading}
                className="w-full py-4 text-sm font-medium tracking-wide transition-colors mb-4"
                style={{ background: '#1A2A1A', color: '#FAF9F6' }}
                onMouseEnter={e => !loading && (e.currentTarget.style.background = '#243424')}
                onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
              >
                {loading ? 'Loading...' : 'Add to Cart'}
              </button>

              <div className="flex flex-wrap gap-5 text-xs" style={{ color: 'rgba(26,42,26,0.5)' }}>
                {['Made in USA', 'FDA Registered', 'Non-GMO', 'Gluten Free'].map((badge, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" style={{ color: '#22c55e' }} />
                    {badge}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
